<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { EasePack } from 'gsap/EasePack'
import type { SectionLogosListRecord } from '~/types/datocms'

defineProps<{
  data: SectionLogosListRecord
  moduleIndex: number
}>()

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  nextTick(() => {
    if (ctxRef.value) {
      gsap.registerPlugin(ScrollTrigger, EasePack)

      ctx = gsap.context(() => {
        const items = gsap.utils.toArray('.fx-sl-logo')
        if (!items.length) return

        const tl = useHorizontalLoop(items, {
          paused: true,
          speed: 0.5,
          repeat: -1
        })

        ScrollTrigger.create({
          trigger: ctxRef.value,
          start: 'top bottom',
          markers: false,
          onEnter: () => {
            tl?.play()
          },
          onLeave: () => {
            tl?.pause()
          },
          onEnterBack: () => {
            tl?.play()
          },
          onLeaveBack: () => {
            tl?.pause()
          }
        })
        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    }
  })
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div ref="ctxRef" class="w-full overflow-hidden bg-yellow py-6">
    <div
      v-if="data.records.length"
      class="relative mx-auto flex w-full items-center justify-center overflow-hidden"
    >
      <div
        v-for="(logo, idx) in data.records"
        :key="idx"
        class="fx-sl-logo flex-shrink-0 transform-gpu px-2 will-change-transform"
      >
        <Image
          v-if="logo.media"
          loading="lazy"
          class="h-[50px] w-auto"
          :src="logo.media?.responsiveImage ? logo.media?.responsiveImage?.src : logo.media?.url"
          :srcset="
            logo.media?.responsiveImage ? logo.media?.responsiveImage?.webpSrcSet : undefined
          "
          :width="
            logo.media?.responsiveImage
              ? logo.media?.responsiveImage?.width
              : (logo.media?.width ?? 150)
          "
          :height="
            logo.media?.responsiveImage
              ? logo.media?.responsiveImage?.height
              : (logo.media?.height ?? 50)
          "
          :alt="logo.media?.alt ?? 'Logo'"
        />
      </div>
      <div
        class="pointer-events-none absolute left-1/2 top-0 h-full w-full max-w-[100vw] -translate-x-1/2"
      ></div>
    </div>
  </div>
</template>
